import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddTeamMemberRequest,
  CreateTeamRequest,
  UpdateTeamMemberRequest,
  UpdateTeamRequest,
} from '@vagusx/clients';
import { getTeamClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

// Teams change rarely — 60s staleness is plenty for list + detail reads
// and saves the focus/reconnect refetch churn on React Query defaults.
const TEAM_CACHE_STALE_MS = 60 * 1000;

/**
 * Hook to fetch all teams for the org.
 */
export function useTeams() {
  return useQuery({
    queryKey: queryKeys.teams.list(),
    queryFn: () => getTeamClient().getAll(),
    staleTime: TEAM_CACHE_STALE_MS,
  });
}

/**
 * Hook to fetch a single team (with full member list).
 */
export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.detail(id ?? ''),
    queryFn: () => getTeamClient().getById(id!),
    enabled: !!id,
    staleTime: TEAM_CACHE_STALE_MS,
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTeamRequest) => getTeamClient().create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamRequest }) =>
      getTeamClient().update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.all });
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(id) });
    },
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTeamClient().deleteTeam(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
  });
}

export function useAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: AddTeamMemberRequest }) =>
      getTeamClient().addMember(teamId, data),
    onSuccess: (_, { teamId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.all });
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) });
    },
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      teamId,
      memberId,
      data,
    }: {
      teamId: string;
      memberId: string;
      data: UpdateTeamMemberRequest;
    }) => getTeamClient().updateMember(teamId, memberId, data),
    onSuccess: (_, { teamId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) });
    },
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) =>
      getTeamClient().removeMember(teamId, memberId),
    onSuccess: (_, { teamId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.all });
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) });
    },
  });
}
